import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { InventorySale, InventorySaleItem, Warehouse, Supplier, InventoryItem } from '../types';
import { formatDate } from '../utils/dateFormat';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const InventorySales: React.FC = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState<InventorySale[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSale, setEditingSale] = useState<InventorySale | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openedViaQuery, setOpenedViaQuery] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [selectedJsonData, setSelectedJsonData] = useState<any>(null);

  const [formData, setFormData] = useState({
    warehouseId: '',
    supplierId: '',
    date: new Date().toISOString().split('T')[0],
    comment: '',
    status: 'DRAFT' as 'DRAFT' | 'POSTED' | 'CANCELLED',
    items: [] as Partial<InventorySaleItem>[],
  });

  useEffect(() => {
    fetchSales();
    fetchWarehouses();
    fetchSuppliers();
    fetchInventoryItems();
  }, []);

  // Открытие на редактирование по query ?open=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (!openId) return;
    setOpenedViaQuery(true);
    (async () => {
      try {
        const sale = await apiGet<InventorySale>(`/inventory-sales/${openId}`);
        setEditingSale(sale);
        setFormData({
          warehouseId: sale.warehouseId,
          supplierId: sale.supplierId || '',
          date: sale.date.split('T')[0],
          comment: sale.comment || '',
          status: sale.status,
          items: (sale.items || []).map(item => ({
            ...item,
            costPrice: item.costPrice ? Math.round(item.costPrice * 100) / 100 : 0,
            totalCost: item.totalCost ? Math.round(item.totalCost * 100) / 100 : 0
          })),
        });
        setShowForm(true);
      } catch (error) {
        console.error('Error loading sale:', error);
      }
    })();
  }, []);

  // Обработка параметров "открыть для редактирования"
  useEffect(() => {
    const openId = searchParams.get('openId');
    if (openId) {
      handleEdit(openId);
    }
  }, [searchParams]);

  const fetchSales = async () => {
    try {
      const data = await apiGet<{ sales?: InventorySale[] }>('/inventory-sales');
      setSales(data.sales || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки продаж ТМЦ');
      console.error('Ошибка:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await apiGet<Warehouse[]>('/warehouses');
      setWarehouses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Ошибка загрузки складов:', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await apiGet<{ suppliers?: Supplier[] }>('/suppliers?limit=1000');
      setSuppliers(data.suppliers || []);
    } catch (err) {
      console.error('Ошибка загрузки поставщиков:', err);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const data = await apiGet<{ items?: InventoryItem[] } | InventoryItem[]>('/inventory-items');
      setInventoryItems(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      console.error('Ошибка загрузки ТМЦ:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingSale) {
        await apiPut(`/inventory-sales/${editingSale.id}`, formData);
      } else {
        await apiPost('/inventory-sales', formData);
      }

      await fetchSales();
      resetForm();
      setError(null);
      setShowForm(false);
    } catch (err: any) {
      setError(err.message || 'Ошибка сохранения продажи ТМЦ');
      console.error('Ошибка:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id: string) => {
    try {
      const sale = await apiGet<InventorySale>(`/inventory-sales/${id}`);
      
      setEditingSale(sale);
      setFormData({
        warehouseId: sale.warehouseId,
        supplierId: sale.supplierId,
        date: sale.date.split('T')[0],
        comment: sale.comment || '',
        status: sale.status,
        items: (sale.items || []).map(item => ({
          ...item,
          costPrice: item.costPrice ? Math.round(item.costPrice * 100) / 100 : 0,
          totalCost: item.totalCost ? Math.round(item.totalCost * 100) / 100 : 0
        })),
      });
      setShowForm(true);
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки продажи');
      console.error('Ошибка:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить эту продажу ТМЦ?')) return;

    try {
      await apiDelete(`/inventory-sales/${id}`);
      await fetchSales();
    } catch (err: any) {
      setError(err.message || 'Ошибка удаления продажи ТМЦ');
      console.error('Ошибка:', err);
    }
  };

  const handleShowJson = (sale: InventorySale) => {
    setSelectedJsonData(sale);
    setShowJsonModal(true);
  };

  const handleCreateCustomerPayment = (sale: InventorySale) => {
    // Переходим на страницу оплат покупателей с предзаполненными данными
    const readableDate = formatDate(sale.date);
    const documentNumber = sale.number || sale.id;
    const totalAmount = sale.items?.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0) || 0;
    
    const params = new URLSearchParams({
      createFrom: sale.id,
      saleType: 'inventory', // Указываем тип продажи
      supplierId: sale.supplierId,
      amount: totalAmount.toString(),
      description: `Оплата по продаже ТМЦ ${documentNumber} от ${readableDate}`,
      date: sale.date.split('T')[0] // Добавляем дату продажи
    });
    navigate(`/customer-payments?${params.toString()}`);
  };

  const resetForm = () => {
    setFormData({
      warehouseId: '',
      supplierId: '',
      date: new Date().toISOString().split('T')[0],
      comment: '',
      status: 'DRAFT',
      items: [],
    });
    setEditingSale(null);
    setShowForm(false);
    setSearchParams({});
    // Если документ открыт по ссылке из отчета (?open=...), возвращаемся по return или history.back()
    if (openedViaQuery) {
      const params = new URLSearchParams(window.location.search);
      const ret = params.get('return');
      if (ret) {
        window.location.href = decodeURIComponent(ret);
        return;
      }
      window.history.back();
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        inventoryItemId: '',
        quantity: 0,
        unit: 'шт',
        unitPrice: 0,
        totalPrice: 0,
        comment: '',
      }],
    }));
  };

  // Функция для загрузки средней стоимости товара
  const loadAverageCost = async (inventoryItemId: string, warehouseId: string) => {
    // Пропускаем загрузку для пустого ID
    if (!inventoryItemId) return 0;
    
    try {
      const data = await apiGet<{ averageCost?: number; quantity?: number }>(`/reports/inventory-item-average-cost/${inventoryItemId}`, {
        warehouseId,
        date: formData.date
      });
      const roundedCost = Math.round((data.averageCost || 0) * 100) / 100;
      setItemAverageCosts(prev => ({
        ...prev,
        [inventoryItemId]: roundedCost
      }));
      setItemBalances(prev => ({
        ...prev,
        [inventoryItemId]: data.quantity || 0
      }));
      return roundedCost;
    } catch (error) {
      console.error('Ошибка загрузки средней стоимости:', error);
    }
    return 0;
  };

  const updateItem = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            // Пересчитываем общую сумму при изменении количества или цены за единицу
            updatedItem.totalPrice = Number(updatedItem.quantity || 0) * Number(updatedItem.unitPrice || 0);
            // Пересчитываем общую стоимость при изменении количества
            if (field === 'quantity' && updatedItem.costPrice) {
              updatedItem.totalCost = Math.round(Number(updatedItem.quantity || 0) * Number(updatedItem.costPrice || 0) * 100) / 100;
            }
          } else if (field === 'totalPrice') {
            // Пересчитываем цену за единицу при изменении общей суммы
            const quantity = Number(updatedItem.quantity || 0);
            if (quantity > 0) {
              updatedItem.unitPrice = Number(updatedItem.totalPrice || 0) / quantity;
            } else {
              updatedItem.unitPrice = 0;
            }
          } else if (field === 'inventoryItemId') {
            // При выборе товара загружаем его среднюю стоимость
            if (value && formData.warehouseId) {
              loadAverageCost(value, formData.warehouseId).then(averageCost => {
                setFormData(prevFormData => ({
                  ...prevFormData,
                  items: prevFormData.items.map((item, idx) => {
                    if (idx === index) {
                      return {
                        ...item,
                        costPrice: Math.round(averageCost * 100) / 100,
                        totalCost: Math.round(averageCost * (Number(item.quantity) || 0) * 100) / 100
                      };
                    }
                    return item;
                  })
                }));
              });
            }
          }
          return updatedItem;
        }
        return item;
      }),
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'POSTED': return 'text-green-600 bg-green-100';
      case 'CANCELLED': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'POSTED': return 'Проведен';
      case 'CANCELLED': return 'Отменен';
      default: return 'Черновик';
    }
  };

  // Состояние для хранения средней стоимости товаров на дату продажи
  const [itemAverageCosts, setItemAverageCosts] = useState<Record<string, number>>({});
  const [itemBalances, setItemBalances] = useState<Record<string, number>>({});

  // Загружаем среднюю стоимость товаров на дату продажи
  useEffect(() => {
    const fetchAverageCosts = async () => {
      if (!formData.date || formData.items.length === 0) return;
      
      const costs: Record<string, number> = {};
      
      for (const item of formData.items) {
        // Пропускаем элементы без выбранного ТМЦ
        if (!item.inventoryItemId) continue;
        
        try {
          const data = await apiGet<{ averageCost?: number }>(`/reports/inventory-item-average-cost/${item.inventoryItemId}`, {
            date: formData.date
          });
          costs[item.inventoryItemId] = Math.round((data.averageCost || 0) * 100) / 100;
        } catch (error) {
          console.error('Ошибка загрузки средней стоимости:', error);
        }
      }
      
      setItemAverageCosts(costs);
    };

    fetchAverageCosts();
  }, [formData.date, formData.items]);

  // Расчет стоимости и маржи
  const calculateTotals = () => {
    const totalRevenue = formData.items.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0);

    const totalCost = formData.items.reduce((sum, item) => {
      const averageCost = itemAverageCosts[item.inventoryItemId] || 0;
      return sum + ((Number(item.quantity) || 0) * averageCost);
    }, 0);

    const margin = totalRevenue - totalCost;
    const marginPercent = totalRevenue > 0 ? (margin / totalRevenue) * 100 : 0;

    return {
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100,
      margin: Math.round(margin * 100) / 100,
      marginPercent: Math.round(marginPercent * 100) / 100
    };
  };

  if (loading && sales.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Продажи ТМЦ</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/purchases')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            📦 Закупка ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-consumptions')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            📦 Расход ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-returns')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            🔄 Возврат ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-transfers')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            🔄 Перемещение ТМЦ
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Добавить продажу
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingSale ? 'Редактировать продажу ТМЦ' : 'Добавить продажу ТМЦ'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Склад *
                  </label>
                  <select
                    value={formData.warehouseId}
                    onChange={(e) => setFormData(prev => ({ ...prev, warehouseId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Выберите склад</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Контрагент *
                  </label>
                  <select
                    value={formData.supplierId}
                    onChange={(e) => setFormData(prev => ({ ...prev, supplierId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Выберите контрагента</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Дата *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Статус
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="DRAFT">Черновик</option>
                    <option value="POSTED">Проведен</option>
                    <option value="CANCELLED">Отменен</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Комментарий
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Позиции товаров
                  </label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    Добавить позицию
                  </button>
                </div>

                {/* Заголовки таблицы */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-2 p-2 bg-gray-100 rounded text-sm font-medium text-gray-700">
                  <div className="md:col-span-2">ТМЦ</div>
                  <div>Количество</div>
                  <div>Ед.изм</div>
                  <div>Цена продажи</div>
                  <div>Сумма</div>
                </div>

                {formData.items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded p-4 mb-2">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                      <div className="md:col-span-2">
                        <select
                          value={item.inventoryItemId || ''}
                          onChange={(e) => updateItem(index, 'inventoryItemId', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          required
                        >
                          <option value="">Выберите ТМЦ</option>
                          {inventoryItems.map(invItem => (
                            <option key={invItem.id} value={invItem.id}>
                              {invItem.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <input
                          type="number"
                          placeholder="Количество"
                          value={item.quantity || ''}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          required
                        />
                      </div>

                      <div>
                        <input
                          type="text"
                          placeholder="Ед.изм"
                          value={item.unit || ''}
                          onChange={(e) => updateItem(index, 'unit', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>

                      <div>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Цена продажи"
                          value={item.unitPrice || ''}
                          onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          required
                        />
                      </div>

                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Сумма"
                          value={item.totalPrice || ''}
                          onChange={(e) => updateItem(index, 'totalPrice', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    {/* Строка с полями стоимости и остатка (только для чтения) */}
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-5 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Остаток на дату:</span>
                        <span className="ml-1 font-medium text-blue-600">
                          {itemBalances[item.inventoryItemId] ? 
                            itemBalances[item.inventoryItemId].toLocaleString('ru-RU', { 
                              minimumFractionDigits: 0, 
                              maximumFractionDigits: 0 
                            }) : '0'} шт
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Стоимость за ед.:</span>
                        <span className="ml-1 font-medium text-red-600">
                          {item.costPrice ? item.costPrice.toLocaleString('ru-RU', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          }) : '0.00'} MDL
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Общая стоимость:</span>
                        <span className="ml-1 font-medium text-red-600">
                          {item.totalCost ? item.totalCost.toLocaleString('ru-RU', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          }) : '0.00'} MDL
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Маржа:</span>
                        <span className={`ml-1 font-medium ${(Number(item.totalPrice) - Number(item.totalCost || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {((Number(item.totalPrice) || 0) - (Number(item.totalCost) || 0)).toLocaleString('ru-RU', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })} MDL
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Маржа %:</span>
                        <span className={`ml-1 font-medium ${((Number(item.totalPrice) || 0) - (Number(item.totalCost) || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Number(item.totalPrice) > 0 ? 
                            (((Number(item.totalPrice) || 0) - (Number(item.totalCost) || 0)) / Number(item.totalPrice) * 100).toLocaleString('ru-RU', { 
                              minimumFractionDigits: 1, 
                              maximumFractionDigits: 1 
                            }) : '0.0'}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Итоговая сумма */}
              {formData.items.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">ИТОГО:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formData.items.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MDL
                    </span>
                  </div>
                </div>
              )}

              {/* Блок с предварительным расчетом */}
              {formData.items.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Предварительный расчет</h3>
                  
                  {/* Детализация по позициям */}
                  <div className="mb-4">
                    <h4 className="text-md font-medium mb-2 text-gray-700">Детализация по позициям:</h4>
                    <div className="space-y-2">
                      {formData.items.map((item, index) => {
                        const inventoryItem = inventoryItems.find(inv => inv.id === item.inventoryItemId);
                        const averageCost = itemAverageCosts[item.inventoryItemId] || 0;
                        const revenue = Number(item.totalPrice) || 0;
                        const cost = averageCost * (Number(item.quantity) || 0);
                        const margin = revenue - cost;
                        const marginPercent = revenue > 0 ? (margin / revenue) * 100 : 0;
                        
                        return (
                          <div key={index} className="bg-white p-3 rounded border">
                            <div className="font-medium text-gray-800 mb-2">
                              {inventoryItem?.name || `Товар ${index + 1}`}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <span className="text-gray-600">Выручка:</span>
                                <span className="ml-1 font-medium text-green-600">
                                  {revenue.toLocaleString('ru-RU', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 2 
                                  })} MDL
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Стоимость:</span>
                                <span className="ml-1 font-medium text-red-600">
                                  {cost.toLocaleString('ru-RU', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 2 
                                  })} MDL
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Маржа:</span>
                                <span className={`ml-1 font-medium ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {margin.toLocaleString('ru-RU', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 2 
                                  })} MDL
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Маржа %:</span>
                                <span className={`ml-1 font-medium ${marginPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {marginPercent.toLocaleString('ru-RU', { 
                                    minimumFractionDigits: 1, 
                                    maximumFractionDigits: 1 
                                  })}%
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Итоговые показатели */}
                  <div className="border-t pt-3">
                    <h4 className="text-md font-medium mb-2 text-gray-700">Итого:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Выручка</div>
                        <div className="text-lg font-bold text-green-600">
                          {calculateTotals().totalRevenue.toLocaleString('ru-RU', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })} MDL
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Стоимость</div>
                        <div className="text-lg font-bold text-red-600">
                          {calculateTotals().totalCost.toLocaleString('ru-RU', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })} MDL
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Маржа</div>
                        <div className={`text-lg font-bold ${calculateTotals().margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {calculateTotals().margin.toLocaleString('ru-RU', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })} MDL
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Маржа %</div>
                        <div className={`text-lg font-bold ${calculateTotals().marginPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {calculateTotals().marginPercent.toLocaleString('ru-RU', { 
                            minimumFractionDigits: 1, 
                            maximumFractionDigits: 1 
                          })}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Номер
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Дата
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Склад
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Контрагент
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Сумма
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sales.map((sale) => {
              const totalAmount = sale.items?.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0) || 0;
              return (
                <tr key={sale.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {sale.number || sale.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(sale.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {sale.warehouse?.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {sale.supplier?.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sale.status)}`}>
                      {getStatusText(sale.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Number(totalAmount || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MDL
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(sale.id)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleShowJson(sale)}
                      className="text-gray-600 hover:text-gray-900 mr-3"
                      title="Показать JSON"
                    >
                      📄
                    </button>
                    <button
                      onClick={() => handleCreateCustomerPayment(sale)}
                      className="text-green-600 hover:text-green-900 mr-3"
                      title="Создать оплату на основании"
                    >
                      💳
                    </button>
                    <button
                      onClick={() => handleDelete(sale.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Модальное окно для отображения JSON */}
      {showJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                JSON содержимое документа
              </h3>
              <button
                onClick={() => setShowJsonModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="bg-gray-100 p-4 rounded-lg">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-auto">
                {JSON.stringify(selectedJsonData, null, 2)}
              </pre>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowJsonModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySales;
